import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  BarChart3, Calendar as CalendarIcon, Download, TrendingUp, TrendingDown, FileText, Globe, List,
  Plus, Target, DollarSign, Users, ShoppingCart, Clock, AlertTriangle, CheckCircle, 
  Percent, ArrowUpRight, ArrowDownRight, RefreshCw, Star, Trash2, Eye, Save, Zap,
  Activity, PieChart as PieChartIcon, LineChart as LineChartIcon, LayoutGrid
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, ComposedChart, Legend } from "recharts";
import axios from "axios";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subDays, subYears, startOfWeek, endOfWeek, startOfQuarter, endOfQuarter } from "date-fns";
import { sv } from "date-fns/locale";

// Quick date presets
const DATE_PRESETS = [
  { label: "Idag", getValue: () => ({ start: new Date(), end: new Date() }) },
  { label: "Igår", getValue: () => ({ start: subDays(new Date(), 1), end: subDays(new Date(), 1) }) },
  { label: "Denna vecka", getValue: () => ({ start: startOfWeek(new Date(), { weekStartsOn: 1 }), end: endOfWeek(new Date(), { weekStartsOn: 1 }) }) },
  { label: "Förra veckan", getValue: () => ({ start: startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 }), end: endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 }) }) },
  { label: "MTD (Denna månad)", getValue: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
  { label: "Förra månaden", getValue: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: "Senaste 30 dagarna", getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { label: "Senaste 90 dagarna", getValue: () => ({ start: subDays(new Date(), 90), end: new Date() }) },
  { label: "QTD (Detta kvartal)", getValue: () => ({ start: startOfQuarter(new Date()), end: new Date() }) },
  { label: "Förra kvartalet", getValue: () => ({ start: startOfQuarter(subMonths(new Date(), 3)), end: endOfQuarter(subMonths(new Date(), 3)) }) },
  { label: "YTD (Detta år)", getValue: () => ({ start: startOfYear(new Date()), end: new Date() }) },
  { label: "Förra året", getValue: () => ({ start: startOfYear(subYears(new Date(), 1)), end: endOfYear(subYears(new Date(), 1)) }) },
  { label: "Senaste 12 månaderna", getValue: () => ({ start: subMonths(new Date(), 12), end: new Date() }) },
];

const Reports = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("kpi");
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [startDate, setStartDate] = useState(format(startOfYear(new Date()), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedPreset, setSelectedPreset] = useState("YTD (Detta år)");
  
  // Report data
  const [kpiData, setKpiData] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [salesByMonth, setSalesByMonth] = useState(null);
  const [salesByCustomer, setSalesByCustomer] = useState(null);
  const [generalLedger, setGeneralLedger] = useState(null);
  const [vatSummary, setVatSummary] = useState(null);
  const [journalEntries, setJournalEntries] = useState(null);
  const [yearEndSummary, setYearEndSummary] = useState(null);
  
  // Custom reports
  const [customReports, setCustomReports] = useState([]);
  const [showCreateReport, setShowCreateReport] = useState(false);
  const [newReport, setNewReport] = useState({
    name: "",
    description: "",
    metrics: [],
    chart_type: "bar"
  });

  // Available metrics for custom reports
  const AVAILABLE_METRICS = [
    { id: "revenue", label: "Intäkter", category: "Ekonomi" },
    { id: "expenses", label: "Kostnader", category: "Ekonomi" },
    { id: "profit", label: "Vinst/Förlust", category: "Ekonomi" },
    { id: "gross_margin", label: "Bruttomarginal %", category: "Ekonomi" },
    { id: "invoiced_amount", label: "Fakturerat belopp", category: "Försäljning" },
    { id: "invoice_count", label: "Antal fakturor", category: "Försäljning" },
    { id: "avg_invoice_value", label: "Snittfaktura", category: "Försäljning" },
    { id: "outstanding_amount", label: "Utestående", category: "Försäljning" },
    { id: "overdue_amount", label: "Förfallet", category: "Försäljning" },
    { id: "customer_count", label: "Antal kunder", category: "Kunder" },
    { id: "new_customers", label: "Nya kunder", category: "Kunder" },
    { id: "customer_retention", label: "Kundbehållning %", category: "Kunder" },
    { id: "avg_days_to_pay", label: "Snitt betalningstid", category: "Kassaflöde" },
    { id: "cash_balance", label: "Kassabehållning", category: "Kassaflöde" },
    { id: "accounts_receivable", label: "Kundfordringar", category: "Kassaflöde" },
    { id: "accounts_payable", label: "Leverantörsskulder", category: "Kassaflöde" },
  ];

  const applyPreset = (preset) => {
    const { start, end } = preset.getValue();
    setStartDate(format(start, "yyyy-MM-dd"));
    setEndDate(format(end, "yyyy-MM-dd"));
    setSelectedPreset(preset.label);
  };

  const fetchKPIData = async () => {
    try {
      // Fetch multiple data sources for KPIs
      const [invoicesRes, customersRes, incomeRes] = await Promise.all([
        axios.get(`${API}/sales-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/income-statement?start_date=${startDate}&end_date=${endDate}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      const invoices = invoicesRes.data || [];
      const customers = customersRes.data || [];
      const income = incomeRes.data || {};
      
      // Filter invoices by date range
      const filteredInvoices = invoices.filter(inv => {
        const invDate = inv.invoice_date;
        return invDate >= startDate && invDate <= endDate;
      });
      
      // Calculate KPIs
      const totalRevenue = income.total_revenue || filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
      const totalExpenses = Math.abs(income.total_cost_of_goods || 0) + Math.abs(income.total_operating_expenses || 0) + Math.abs(income.total_personnel_costs || 0);
      const netProfit = income.net_profit || (totalRevenue - totalExpenses);
      
      const paidInvoices = filteredInvoices.filter(inv => inv.status === "paid");
      const outstandingInvoices = invoices.filter(inv => ["sent", "overdue"].includes(inv.status));
      const overdueInvoices = invoices.filter(inv => inv.status === "overdue");
      
      // Calculate average days to pay
      let totalDaysToPay = 0;
      let paidCount = 0;
      paidInvoices.forEach(inv => {
        if (inv.paid_date && inv.invoice_date) {
          const days = Math.floor((new Date(inv.paid_date) - new Date(inv.invoice_date)) / (1000 * 60 * 60 * 24));
          if (days >= 0) {
            totalDaysToPay += days;
            paidCount++;
          }
        }
      });
      
      // Monthly trend data
      const monthlyData = {};
      filteredInvoices.forEach(inv => {
        const month = inv.invoice_date?.substring(0, 7);
        if (month) {
          if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, count: 0 };
          monthlyData[month].revenue += inv.total || 0;
          monthlyData[month].count++;
        }
      });
      
      setKpiData({
        totalRevenue,
        totalExpenses,
        netProfit,
        grossMargin: totalRevenue > 0 ? ((totalRevenue - totalExpenses) / totalRevenue * 100).toFixed(1) : 0,
        invoiceCount: filteredInvoices.length,
        avgInvoiceValue: filteredInvoices.length > 0 ? totalRevenue / filteredInvoices.length : 0,
        outstandingAmount: outstandingInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
        overdueAmount: overdueInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
        overdueCount: overdueInvoices.length,
        customerCount: customers.length,
        avgDaysToPay: paidCount > 0 ? Math.round(totalDaysToPay / paidCount) : 0,
        paidInvoiceCount: paidInvoices.length,
        collectionRate: filteredInvoices.length > 0 ? ((paidInvoices.length / filteredInvoices.length) * 100).toFixed(1) : 0,
        monthlyTrend: Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)),
        revenueChange: 0, // Would need previous period data
        profitChange: 0,
      });
    } catch (error) {
      console.error("Error fetching KPI data:", error);
    }
  };

  const fetchReport = async (reportType) => {
    setLoading(true);
    try {
      let response;
      switch (reportType) {
        case "kpi":
          await fetchKPIData();
          break;
        case "income":
          response = await axios.get(`${API}/reports/income-statement?start_date=${startDate}&end_date=${endDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIncomeStatement(response.data);
          break;
        case "balance":
          response = await axios.get(`${API}/reports/balance-sheet?date=${endDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBalanceSheet(response.data);
          break;
        case "sales-month":
          response = await axios.get(`${API}/reports/sales-by-month?year=${year}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSalesByMonth(response.data);
          break;
        case "sales-customer":
          response = await axios.get(`${API}/reports/sales-by-customer?start_date=${startDate}&end_date=${endDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSalesByCustomer(response.data);
          break;
        case "ledger":
          response = await axios.get(`${API}/reports/general-ledger?start_date=${startDate}&end_date=${endDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setGeneralLedger(response.data);
          break;
        case "vat":
          response = await axios.get(`${API}/reports/vat-summary?start_date=${startDate}&end_date=${endDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setVatSummary(response.data);
          break;
        case "journal":
          response = await axios.get(`${API}/reports/journal-entries?start_date=${startDate}&end_date=${endDate}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setJournalEntries(response.data);
          break;
        case "year-end":
          response = await axios.get(`${API}/reports/year-end-summary?fiscal_year=${year}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setYearEndSummary(response.data);
          break;
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Kunde inte hämta rapport");
    } finally {
      setLoading(false);
    }
  };

  // Load saved custom reports
  useEffect(() => {
    const saved = localStorage.getItem('yubi_custom_reports');
    if (saved) {
      try {
        setCustomReports(JSON.parse(saved));
      } catch {}
    }
  }, []);

  useEffect(() => {
    fetchReport(activeTab);
  }, [activeTab, startDate, endDate, year]);

  const saveCustomReport = () => {
    if (!newReport.name || newReport.metrics.length === 0) {
      toast.error("Ange namn och välj minst ett nyckeltal");
      return;
    }
    
    const report = { ...newReport, id: Date.now().toString(), created: new Date().toISOString() };
    const updated = [...customReports, report];
    setCustomReports(updated);
    localStorage.setItem('yubi_custom_reports', JSON.stringify(updated));
    setShowCreateReport(false);
    setNewReport({ name: "", description: "", metrics: [], chart_type: "bar" });
    toast.success("Rapport skapad!");
  };

  const deleteCustomReport = (id) => {
    const updated = customReports.filter(r => r.id !== id);
    setCustomReports(updated);
    localStorage.setItem('yubi_custom_reports', JSON.stringify(updated));
    toast.success("Rapport borttagen");
  };

  const toggleMetric = (metricId) => {
    setNewReport(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metricId)
        ? prev.metrics.filter(m => m !== metricId)
        : [...prev.metrics, metricId]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => `${value}%`;

  const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

  // Generate years from 2020 to current+1
  const years = [];
  for (let y = 2020; y <= new Date().getFullYear() + 1; y++) {
    years.push(y);
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('reports.title')}</h1>
          <p className="text-slate-500">{t('reports.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreateReport(true)} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus size={16} className="mr-2" />
          Skapa egen rapport
        </Button>
      </div>

      {/* Enhanced Date Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Quick presets */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Snabbval period</Label>
              <div className="flex flex-wrap gap-2">
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant={selectedPreset === preset.label ? "default" : "outline"}
                    size="sm"
                    onClick={() => applyPreset(preset)}
                    className={selectedPreset === preset.label ? "bg-indigo-600" : ""}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Custom date range */}
            <div className="flex items-center gap-4 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">{t('reports.from')}:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="report-start-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(startDate)}
                      onSelect={(date) => {
                        if (date) {
                          setStartDate(format(date, "yyyy-MM-dd"));
                          setSelectedPreset("");
                        }
                      }}
                      locale={sv}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">{t('reports.to')}:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="report-end-date">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(endDate)}
                      onSelect={(date) => {
                        if (date) {
                          setEndDate(format(date, "yyyy-MM-dd"));
                          setSelectedPreset("");
                        }
                      }}
                      locale={sv}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">{t('reports.year')}:</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger className="w-24" data-testid="report-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchReport(activeTab)} data-testid="refresh-report">
                <RefreshCw size={14} className="mr-1" />
                Uppdatera
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="kpi" data-testid="tab-kpi" className="flex items-center gap-1">
            <Zap size={14} />
            Nyckeltal
          </TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-income">
            <TrendingUp size={14} className="mr-1" />
            {t('reports.incomeStatement')}
          </TabsTrigger>
          <TabsTrigger value="balance" data-testid="tab-balance">
            <BarChart3 size={14} className="mr-1" />
            {t('reports.balanceSheet')}
          </TabsTrigger>
          <TabsTrigger value="sales-month" data-testid="tab-sales-month">
            {t('reports.salesByMonth')}
          </TabsTrigger>
          <TabsTrigger value="sales-customer" data-testid="tab-sales-customer">
            {t('reports.salesByCustomer')}
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">
            {t('reports.generalLedger')}
          </TabsTrigger>
          <TabsTrigger value="vat" data-testid="tab-vat">
            <Globe size={14} className="mr-1" />
            {t('reports.vat')}
          </TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">
            <List size={14} className="mr-1" />
            {t('reports.journalEntries')}
          </TabsTrigger>
          <TabsTrigger value="year-end" data-testid="tab-year-end">
            <FileText size={14} className="mr-1" />
            {t('reports.yearEnd')}
          </TabsTrigger>
          <TabsTrigger value="sie" data-testid="tab-sie">
            <Download size={14} className="mr-1" />
            {t('reports.sieExport')}
          </TabsTrigger>
          {customReports.length > 0 && (
            <TabsTrigger value="custom" data-testid="tab-custom" className="flex items-center gap-1">
              <Star size={14} />
              Egna ({customReports.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* KPI Dashboard */}
        <TabsContent value="kpi" className="mt-6">
          <div className="space-y-6">
            {/* Main KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Intäkter</p>
                      <p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(kpiData?.totalRevenue)}</p>
                      <p className="text-xs text-green-600 mt-1">{selectedPreset || `${startDate} - ${endDate}`}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-green-200 flex items-center justify-center">
                      <TrendingUp className="text-green-600" size={20} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-red-600 font-medium uppercase tracking-wide">Kostnader</p>
                      <p className="text-2xl font-bold text-red-700 mt-1">{formatCurrency(kpiData?.totalExpenses)}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-red-200 flex items-center justify-center">
                      <TrendingDown className="text-red-600" size={20} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className={`bg-gradient-to-br ${(kpiData?.netProfit || 0) >= 0 ? 'from-indigo-50 to-indigo-100 border-indigo-200' : 'from-amber-50 to-amber-100 border-amber-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-xs font-medium uppercase tracking-wide ${(kpiData?.netProfit || 0) >= 0 ? 'text-indigo-600' : 'text-amber-600'}`}>
                        {(kpiData?.netProfit || 0) >= 0 ? 'Vinst' : 'Förlust'}
                      </p>
                      <p className={`text-2xl font-bold mt-1 ${(kpiData?.netProfit || 0) >= 0 ? 'text-indigo-700' : 'text-amber-700'}`}>
                        {formatCurrency(Math.abs(kpiData?.netProfit || 0))}
                      </p>
                    </div>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${(kpiData?.netProfit || 0) >= 0 ? 'bg-indigo-200' : 'bg-amber-200'}`}>
                      <Target className={(kpiData?.netProfit || 0) >= 0 ? 'text-indigo-600' : 'text-amber-600'} size={20} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Bruttomarginal</p>
                      <p className="text-2xl font-bold text-purple-700 mt-1">{kpiData?.grossMargin}%</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-purple-200 flex items-center justify-center">
                      <Percent className="text-purple-600" size={20} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Card>
                <CardContent className="p-3">
                  <p className="text-xs text-slate-500">Antal fakturor</p>
                  <p className="text-xl font-bold">{kpiData?.invoiceCount || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-xs text-slate-500">Snittfaktura</p>
                  <p className="text-xl font-bold">{formatCurrency(kpiData?.avgInvoiceValue)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-xs text-slate-500">Utestående</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(kpiData?.outstandingAmount)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-xs text-slate-500">Förfallet</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(kpiData?.overdueAmount)}</p>
                  {kpiData?.overdueCount > 0 && (
                    <p className="text-xs text-red-500">{kpiData.overdueCount} fakturor</p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-xs text-slate-500">Snitt betalningstid</p>
                  <p className="text-xl font-bold">{kpiData?.avgDaysToPay || 0} dagar</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3">
                  <p className="text-xs text-slate-500">Betalningsgrad</p>
                  <p className="text-xl font-bold text-green-600">{kpiData?.collectionRate || 0}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Trend Chart */}
            {kpiData?.monthlyTrend?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Intäktsutveckling</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={kpiData.monthlyTrend}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Area type="monotone" dataKey="revenue" stroke="#4F46E5" fill="url(#colorRevenue)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Income Statement */}
        <TabsContent value="income" className="mt-6">
          <Card className="card-base" data-testid="income-statement">
            <CardHeader>
              <CardTitle>{t('reports.incomeStatement')}</CardTitle>
              <CardDescription>{t('reports.period')}: {startDate} - {endDate}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : incomeStatement ? (
                <div className="space-y-6">
                  {/* Revenue */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{t('reports.operatingIncome')}</h3>
                    <div className="space-y-1">
                      {incomeStatement.revenue?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                          <span className="font-mono">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium border-t pt-2 mt-2">
                        <span>{t('common.total')}</span>
                        <span className="font-mono text-green-600">{formatCurrency(incomeStatement.total_revenue)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Costs */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{t('reports.operatingExpenses')}</h3>
                    <div className="space-y-1">
                      {incomeStatement.cost_of_goods?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                          <span className="font-mono text-red-600">-{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      {incomeStatement.operating_expenses?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                          <span className="font-mono text-red-600">-{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      {incomeStatement.personnel_costs?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                          <span className="font-mono text-red-600">-{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Result */}
                  <div className="border-t-2 pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>{t('reports.netProfit')}</span>
                      <span className={`font-mono ${incomeStatement.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {formatCurrency(incomeStatement.net_profit)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">{t('common.noData')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="balance" className="mt-6">
          <Card className="card-base" data-testid="balance-sheet">
            <CardHeader>
              <CardTitle>Balansräkning</CardTitle>
              <CardDescription>Per {endDate}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : balanceSheet ? (
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Tillgångar</h3>
                    <div className="space-y-1">
                      {/* Fixed Assets */}
                      {balanceSheet.assets?.fixed_assets && (
                        <>
                          <div className="font-medium text-slate-700 mt-2">Anläggningstillgångar</div>
                          {balanceSheet.assets.fixed_assets.tangible?.map((item, idx) => (
                            <div key={`tangible-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.assets.fixed_assets.intangible?.map((item, idx) => (
                            <div key={`intangible-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.assets.fixed_assets.financial?.map((item, idx) => (
                            <div key={`financial-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm py-1 pl-4 font-medium">
                            <span>Summa anläggningstillgångar</span>
                            <span className="font-mono">{formatCurrency(balanceSheet.assets.fixed_assets.total)}</span>
                          </div>
                        </>
                      )}
                      {/* Current Assets */}
                      {balanceSheet.assets?.current_assets && (
                        <>
                          <div className="font-medium text-slate-700 mt-4">Omsättningstillgångar</div>
                          {balanceSheet.assets.current_assets.inventory?.map((item, idx) => (
                            <div key={`inventory-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.assets.current_assets.receivables?.map((item, idx) => (
                            <div key={`receivables-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.assets.current_assets.cash?.map((item, idx) => (
                            <div key={`cash-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm py-1 pl-4 font-medium">
                            <span>Summa omsättningstillgångar</span>
                            <span className="font-mono">{formatCurrency(balanceSheet.assets.current_assets.total)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between font-medium border-t pt-2 mt-2">
                        <span>Summa tillgångar</span>
                        <span className="font-mono">{formatCurrency(balanceSheet.assets?.total || 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Eget kapital och skulder</h3>
                    <div className="space-y-1">
                      {/* Equity */}
                      {balanceSheet.equity_and_liabilities?.equity && (
                        <>
                          <div className="font-medium text-slate-700 mt-2">Eget kapital</div>
                          {balanceSheet.equity_and_liabilities.equity.share_capital?.map((item, idx) => (
                            <div key={`share-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.equity_and_liabilities.equity.reserves?.map((item, idx) => (
                            <div key={`reserves-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.equity_and_liabilities.equity.retained_earnings?.map((item, idx) => (
                            <div key={`retained-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.equity_and_liabilities.equity.profit_loss?.map((item, idx) => (
                            <div key={`profit-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm py-1 pl-4 font-medium">
                            <span>Summa eget kapital</span>
                            <span className="font-mono">{formatCurrency(balanceSheet.equity_and_liabilities.equity.total)}</span>
                          </div>
                        </>
                      )}
                      {/* Liabilities */}
                      {balanceSheet.equity_and_liabilities?.liabilities && (
                        <>
                          <div className="font-medium text-slate-700 mt-4">Skulder</div>
                          {balanceSheet.equity_and_liabilities.liabilities.long_term?.map((item, idx) => (
                            <div key={`longterm-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.equity_and_liabilities.liabilities.short_term?.map((item, idx) => (
                            <div key={`shortterm-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          {balanceSheet.equity_and_liabilities.liabilities.tax_liabilities?.map((item, idx) => (
                            <div key={`tax-${idx}`} className="flex justify-between text-sm py-1 pl-4">
                              <span className="text-slate-600">{item.account_number} {item.account_name}</span>
                              <span className="font-mono">{formatCurrency(item.balance)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm py-1 pl-4 font-medium">
                            <span>Summa skulder</span>
                            <span className="font-mono">{formatCurrency(balanceSheet.equity_and_liabilities.liabilities.total)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between font-medium border-t pt-2 mt-2">
                        <span>Summa eget kapital och skulder</span>
                        <span className="font-mono">{formatCurrency(balanceSheet.equity_and_liabilities?.total || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Ingen data att visa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Month */}
        <TabsContent value="sales-month" className="mt-6">
          <Card className="card-base" data-testid="sales-by-month">
            <CardHeader>
              <CardTitle>Försäljning per månad</CardTitle>
              <CardDescription>År {year}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : salesByMonth ? (
                <div>
                  <div className="h-[300px] mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={salesByMonth.months}>
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `${v/1000}k`} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="total" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Total försäljning</p>
                      <p className="text-2xl font-bold">{formatCurrency(salesByMonth.total)}</p>
                    </Card>
                    <Card className="bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Total moms</p>
                      <p className="text-2xl font-bold">{formatCurrency(salesByMonth.total_vat)}</p>
                    </Card>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Ingen data att visa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Customer */}
        <TabsContent value="sales-customer" className="mt-6">
          <Card className="card-base" data-testid="sales-by-customer">
            <CardHeader>
              <CardTitle>Försäljning per kund</CardTitle>
              <CardDescription>Period: {startDate} - {endDate}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : salesByCustomer ? (
                <div className="grid grid-cols-2 gap-6">
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={salesByCustomer.customers?.slice(0, 6)}
                          dataKey="total"
                          nameKey="customer_name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name?.substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                        >
                          {salesByCustomer.customers?.slice(0, 6).map((_, idx) => (
                            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          <th>Kund</th>
                          <th className="text-right">Fakturor</th>
                          <th className="text-right">Total</th>
                          <th className="text-right">Utestående</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesByCustomer.customers?.map((c, idx) => (
                          <tr key={idx}>
                            <td className="truncate max-w-[150px]">{c.customer_name}</td>
                            <td className="text-right">{c.invoice_count}</td>
                            <td className="text-right font-mono">{formatCurrency(c.total)}</td>
                            <td className={`text-right font-mono ${c.outstanding > 0 ? "text-amber-600" : ""}`}>
                              {formatCurrency(c.outstanding)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Ingen data att visa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Ledger */}
        <TabsContent value="ledger" className="mt-6">
          <Card className="card-base" data-testid="general-ledger">
            <CardHeader>
              <CardTitle>Huvudbok</CardTitle>
              <CardDescription>Period: {startDate} - {endDate}</CardDescription>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : generalLedger?.accounts?.length > 0 ? (
                <div className="space-y-6">
                  {generalLedger.accounts.map((account, idx) => (
                    <div key={idx} className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 flex justify-between items-center">
                        <span className="font-medium">{account.account_number} - {account.account_name}</span>
                        <span className="font-mono font-medium">Saldo: {formatCurrency(account.balance)}</span>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2 w-24">Datum</th>
                            <th className="text-left p-2 w-16">Ver</th>
                            <th className="text-left p-2">Beskrivning</th>
                            <th className="text-right p-2 w-24">Debet</th>
                            <th className="text-right p-2 w-24">Kredit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {account.entries?.map((entry, eIdx) => (
                            <tr key={eIdx} className="border-b last:border-0">
                              <td className="p-2 font-mono text-xs">{entry.date}</td>
                              <td className="p-2">#{entry.entry_number}</td>
                              <td className="p-2 truncate max-w-[200px]">{entry.description}</td>
                              <td className="p-2 text-right font-mono">{entry.debit > 0 ? formatCurrency(entry.debit) : ""}</td>
                              <td className="p-2 text-right font-mono">{entry.credit > 0 ? formatCurrency(entry.credit) : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50">
                          <tr>
                            <td colSpan="3" className="p-2 text-right font-medium">Summa:</td>
                            <td className="p-2 text-right font-mono font-medium">{formatCurrency(account.total_debit)}</td>
                            <td className="p-2 text-right font-mono font-medium">{formatCurrency(account.total_credit)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Ingen data att visa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* VAT Summary */}
        <TabsContent value="vat" className="mt-6">
          <Card className="card-base" data-testid="vat-summary">
            <CardHeader>
              <CardTitle>Momsöversikt</CardTitle>
              <CardDescription>Period: {startDate} - {endDate}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : vatSummary ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-green-50 p-4">
                      <p className="text-sm text-green-700">Utgående moms</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(vatSummary.summary?.output_vat)}</p>
                    </Card>
                    <Card className="bg-red-50 p-4">
                      <p className="text-sm text-red-700">Ingående moms</p>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(vatSummary.summary?.input_vat)}</p>
                    </Card>
                    <Card className="bg-indigo-50 p-4">
                      <p className="text-sm text-indigo-700">Moms att betala</p>
                      <p className="text-2xl font-bold text-indigo-700">{formatCurrency(vatSummary.summary?.vat_payable)}</p>
                    </Card>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3">Moms per skattesats</h3>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Momssats</th>
                          <th className="text-right">Underlag</th>
                          <th className="text-right">Moms</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vatSummary.vat_by_rate?.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.rate}%</td>
                            <td className="text-right font-mono">{formatCurrency(r.base)}</td>
                            <td className="text-right font-mono">{formatCurrency(r.vat)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">Ingen data att visa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Journal Entries */}
        <TabsContent value="journal" className="mt-6">
          <Card className="card-base" data-testid="journal-entries-list">
            <CardHeader>
              <CardTitle>Verifikationslista</CardTitle>
              <CardDescription>
                Period: {startDate} - {endDate} 
                {journalEntries && ` (${journalEntries.count} verifikationer)`}
              </CardDescription>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : journalEntries?.entries?.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-16">Ver</th>
                      <th className="w-24">Datum</th>
                      <th>Beskrivning</th>
                      <th className="text-right w-28">Debet</th>
                      <th className="text-right w-28">Kredit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journalEntries.entries.map((entry, idx) => (
                      <tr key={idx}>
                        <td className="font-mono text-indigo-600">#{entry.entry_number}</td>
                        <td className="font-mono text-sm">{entry.date}</td>
                        <td className="truncate max-w-[300px]">{entry.description}</td>
                        <td className="text-right font-mono">{formatCurrency(entry.total_debit)}</td>
                        <td className="text-right font-mono">{formatCurrency(entry.total_credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-medium">
                    <tr>
                      <td colSpan="3" className="text-right">Summa:</td>
                      <td className="text-right font-mono">{formatCurrency(journalEntries.total_debit)}</td>
                      <td className="text-right font-mono">{formatCurrency(journalEntries.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-slate-500 text-center py-8">Inga verifikationer att visa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Year-End Closing */}
        <TabsContent value="year-end" className="mt-6">
          <YearEndClosing token={token} year={year} />
        </TabsContent>

        {/* SIE Export */}
        <TabsContent value="sie" className="mt-6">
          <SIEExport token={token} year={year} />
        </TabsContent>

        {/* Custom Reports */}
        <TabsContent value="custom" className="mt-6">
          <div className="space-y-4">
            {customReports.map(report => (
              <Card key={report.id} className="card-base">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Star size={16} className="text-amber-500" />
                        {report.name}
                      </CardTitle>
                      {report.description && (
                        <CardDescription>{report.description}</CardDescription>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteCustomReport(report.id)} className="text-red-500">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {report.metrics.map(metricId => {
                      const metric = AVAILABLE_METRICS.find(m => m.id === metricId);
                      return metric ? (
                        <Badge key={metricId} variant="outline">{metric.label}</Badge>
                      ) : null;
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Skapad: {new Date(report.created).toLocaleDateString('sv-SE')}
                  </p>
                </CardContent>
              </Card>
            ))}
            {customReports.length === 0 && (
              <Card className="card-base">
                <CardContent className="py-12 text-center">
                  <Star className="mx-auto mb-4 text-slate-300" size={48} />
                  <p className="text-slate-500">Inga egna rapporter skapade</p>
                  <Button onClick={() => setShowCreateReport(true)} variant="outline" className="mt-4">
                    <Plus size={16} className="mr-2" />
                    Skapa din första rapport
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Custom Report Dialog */}
      <Dialog open={showCreateReport} onOpenChange={setShowCreateReport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Skapa egen rapport</DialogTitle>
            <DialogDescription>
              Välj de nyckeltal du vill följa och skapa din egen anpassade rapport
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rapportnamn *</Label>
                <Input
                  value={newReport.name}
                  onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                  placeholder="T.ex. Månadsöversikt"
                />
              </div>
              <div className="space-y-2">
                <Label>Diagramtyp</Label>
                <Select value={newReport.chart_type} onValueChange={(v) => setNewReport({ ...newReport, chart_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bar">Stapeldiagram</SelectItem>
                    <SelectItem value="line">Linjediagram</SelectItem>
                    <SelectItem value="pie">Cirkeldiagram</SelectItem>
                    <SelectItem value="cards">Kort (KPI)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Beskrivning (valfritt)</Label>
              <Input
                value={newReport.description}
                onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                placeholder="Kort beskrivning av rapporten"
              />
            </div>

            <div className="space-y-2">
              <Label>Välj nyckeltal ({newReport.metrics.length} valda)</Label>
              <div className="border rounded-lg p-4 max-h-[300px] overflow-y-auto">
                {["Ekonomi", "Försäljning", "Kunder", "Kassaflöde"].map(category => (
                  <div key={category} className="mb-4 last:mb-0">
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-2">{category}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_METRICS.filter(m => m.category === category).map(metric => (
                        <label
                          key={metric.id}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                            newReport.metrics.includes(metric.id)
                              ? 'bg-indigo-50 border-indigo-300'
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <Checkbox
                            checked={newReport.metrics.includes(metric.id)}
                            onCheckedChange={() => toggleMetric(metric.id)}
                          />
                          <span className="text-sm">{metric.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateReport(false)}>
              Avbryt
            </Button>
            <Button onClick={saveCustomReport} className="bg-indigo-600 hover:bg-indigo-700">
              <Save size={16} className="mr-2" />
              Spara rapport
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// SIE Export Component
const SIEExport = ({ token, year }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { t } = useTranslation();

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/export/sie/preview?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
    } catch (error) {
      toast.error("Kunde inte hämta förhandsgranskning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, [year]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/export/sie?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bokforing_${year}.se`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("SIE-fil exporterad");
    } catch (error) {
      toast.error("Kunde inte exportera SIE-fil");
    } finally {
      setDownloading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(num || 0);
  };

  return (
    <Card className="card-base" data-testid="sie-export">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download size={20} />
          SIE-export
        </CardTitle>
        <CardDescription>
          Exportera bokföringen till SIE4-format för revisorer och Skatteverket
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Förbereder export...</p>
          </div>
        ) : preview ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Företag</p>
                <p className="font-medium">{preview.company_name || "-"}</p>
                <p className="text-xs text-slate-400 font-mono">{preview.org_number}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Räkenskapsår</p>
                <p className="font-medium">{preview.fiscal_year}</p>
                <p className="text-xs text-slate-400">{preview.period?.start} - {preview.period?.end}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Konton</p>
                <p className="text-2xl font-bold text-indigo-600">{preview.accounts_count}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Verifikationer</p>
                <p className="text-2xl font-bold text-indigo-600">{preview.entries_count}</p>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-indigo-600 font-medium">Total debet</p>
                  <p className="text-lg font-bold font-mono">{formatNumber(preview.total_debit)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-indigo-400">Format</p>
                  <p className="font-mono font-bold text-indigo-600">{preview.format}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-indigo-600 font-medium">Total kredit</p>
                  <p className="text-lg font-bold font-mono">{formatNumber(preview.total_credit)}</p>
                </div>
              </div>
            </div>

            <Button 
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-sie-btn"
            >
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Exporterar...
                </>
              ) : (
                <>
                  <Download size={16} className="mr-2" />
                  Ladda ner SIE-fil ({preview.fiscal_year})
                </>
              )}
            </Button>
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">Ingen data att exportera</p>
        )}
      </CardContent>
    </Card>
  );
};

export default Reports;

// Year-End Closing Component
const YearEndClosing = ({ token, year }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const { t } = useTranslation();

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/year-end-summary?fiscal_year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      toast.error(t('reports.errorFetching'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [year]);

  const handlePerformClosing = async () => {
    if (!window.confirm(t('reports.confirmYearEnd'))) return;
    
    setClosing(true);
    try {
      const response = await axios.post(`${API}/reports/year-end-closing?fiscal_year=${year}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${t('reports.yearEndCompleted')} - ${response.data.result_type}: ${formatCurrency(Math.abs(response.data.net_result))}`);
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('reports.yearEndFailed'));
    } finally {
      setClosing(false);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(num || 0);
  };

  return (
    <Card className="card-base" data-testid="year-end-closing">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText size={20} />
          {t('reports.yearEnd')} {year}
        </CardTitle>
        <CardDescription>{t('reports.yearEndDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">{t('common.loading')}...</p>
          </div>
        ) : summary ? (
          <div className="space-y-6">
            {summary.already_closed && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <FileText className="text-green-600" size={16} />
                </div>
                <div>
                  <p className="font-medium text-green-800">{t('reports.yearEndAlreadyDone')}</p>
                  <p className="text-sm text-green-600">{t('reports.yearEndAlreadyDoneDesc')}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <p className="text-xs text-green-600 uppercase tracking-wide font-medium">{t('reports.totalRevenue')}</p>
                <p className="text-2xl font-bold text-green-700 font-mono">{formatCurrency(summary.total_revenue)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                <p className="text-xs text-red-600 uppercase tracking-wide font-medium">{t('reports.totalExpenses')}</p>
                <p className="text-2xl font-bold text-red-700 font-mono">{formatCurrency(summary.total_expenses)}</p>
              </div>
              <div className={`rounded-lg p-4 border ${summary.net_result >= 0 ? 'bg-indigo-50 border-indigo-100' : 'bg-amber-50 border-amber-100'}`}>
                <p className={`text-xs uppercase tracking-wide font-medium ${summary.net_result >= 0 ? 'text-indigo-600' : 'text-amber-600'}`}>
                  {summary.result_type}
                </p>
                <p className={`text-2xl font-bold font-mono ${summary.net_result >= 0 ? 'text-indigo-700' : 'text-amber-700'}`}>
                  {formatCurrency(Math.abs(summary.net_result))}
                </p>
              </div>
            </div>

            {!summary.already_closed && (
              <Button 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handlePerformClosing}
                disabled={closing}
                data-testid="perform-year-end-btn"
              >
                {closing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t('reports.performingYearEnd')}...
                  </>
                ) : (
                  <>
                    <FileText size={16} className="mr-2" />
                    {t('reports.performYearEnd')} {year}
                  </>
                )}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-8">{t('common.noData')}</p>
        )}
      </CardContent>
    </Card>
  );
};
